import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { SagaObject } from '@models/imported/SagaBase/SagaObject';
import { Actor } from '@models/imported/SagaSchema/Actor';
import { CommunicationEquipment } from '@models/imported/SagaSchema/CommunicationEquipment';
import { ListUnitTypes } from '@models/imported/SagaSchema/ListObjects/ListUnitTypes';
import { Vehicle } from '@models/imported/SagaSchema/Vehicle';
import { FetchListService } from '@services/fetchlist/fetchlist.service';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ResourceNamePipe } from '../../pipes/resource-name.pipe';
import { UnitCreationService } from '../../services/unit-creation/unit-creation.service';
import { UnitEditorService } from '../../services/unit-editor/unit-editor.service';

@Component({
  selector: 'app-unit-summary',
  templateUrl: './unit-summary.component.html',
  styleUrls: ['./unit-summary.component.scss']
})
export class UnitSummaryComponent implements OnInit {
  unitTypes: ListUnitTypes[];

  get mainResources(): SagaObject[] {
    return [...this.unitEditor.editor.vehicles, ...this.unitEditor.editor.actors, ...this.unitEditor.editor.communicationEquipments];
  }

  get communicationEquipments(): CommunicationEquipment[] {
    return this.unitEditor.editor.communicationEquipments;
  }

  get agents(): Actor[] {
    return this.unitEditor.editor.actors;
  }

  get vehicles(): Vehicle[] {
    return this.unitEditor.editor.vehicles;
  }

  get positionProviders(): SagaObject[] {
    return [...this.unitEditor.editor.communicationEquipments, ...this.unitEditor.editor.vehicles, ...this.unitEditor.editor.actors];
  }

  get mainResource(): SagaObject {
    return this.unitEditor.editor.mainResource;
  }

  get agent(): Actor {
    return this.unitEditor.editor.agent;
  }

  get positionProvider(): SagaObject {
    return this.unitEditor.editor.positionProvider;
  }

  get communicationEquipment(): CommunicationEquipment {
    return this.unitEditor.editor.communicationEquipment;
  }

  get name(): string {
    return this.unitEditor.editor.name;
  }

  get unitType(): ListUnitTypes {
    return this.unitEditor.editor.unitType;
  }

  httpError: HttpErrorResponse = null;
  error = false;

  constructor(
    private unitEditor: UnitEditorService,
    private modalController: ModalController,
    private unitCreationService: UnitCreationService,
    private fetchListService: FetchListService,
    private resourceName: ResourceNamePipe
  ) {}

  ngOnInit() {
    this.unitTypes = this.fetchListService.getAllRows(ListUnitTypes);
    this.setDefaultValues();
  }

  private setDefaultValues() {
    const editor = this.unitEditor.editor;
    if (this.mainResources.length && editor.mainResource === null) {
      this.setMainResource(this.mainResources[0]);
    }
    if (this.positionProviders.length && editor.positionProvider === null) {
      editor.positionProvider = this.positionProviders[0];
    }
    if (editor.communicationEquipments.length && editor.communicationEquipment === null) {
      editor.communicationEquipment = editor.communicationEquipments[0];
    }
    if (editor.actors.length && editor.agent === null) {
      editor.agent = editor.actors[0];
    }
    if (this.unitTypes.length && editor.unitType === null) {
      editor.unitType = this.unitTypes[0];
    }
  }

  private setMainResource(mainResource: SagaObject) {
    this.unitEditor.editor.mainResource = mainResource;
    this.unitEditor.editor.name = this.resourceName.transform(mainResource);
  }

  onUnitTypeSelectionChange($event) {
    this.unitEditor.editor.unitType = $event.target.value;
  }

  onMainResourceSelectionChange($event) {
    this.setMainResource($event.target.value);
  }

  onMainAgentSelectionChange($event) {
    this.unitEditor.editor.agent = $event.target.value;
  }

  onCommunicationEquipmentSelectionChange($event) {
    this.unitEditor.editor.communicationEquipment = $event.target.value;
  }

  onPositionProviderSelectionChange($event) {
    this.unitEditor.editor.positionProvider = $event.target.value;
  }

  cancel() {
    this.modalController.dismiss();
  }

  confirm() {
    this.httpError = null;
    this.error = false;
    this.unitCreationService
      .create(this.unitEditor.editor)
      .pipe(catchError(error => this.handleError(error)))
      .subscribe(res => this.parse(res));
  }

  private handleError(error: HttpErrorResponse) {
    this.httpError = error;
    return of(null);
  }

  private parse(res: boolean) {
    if (res === true) {
      this.modalController.dismiss(res);
    } else if (this.httpError === null) {
      this.error = true;
    }
  }
}
